/**
 * "Back to the Insurance Suite" (SDD-012, U-5').
 *
 * The console is opened from the legacy admin in a named window, so the legacy tab is usually
 * this window's opener. Back then does what a user means by "back" after opening something in
 * a new window: it puts the legacy tab in front again and closes this one, so the legacy keeps
 * its filters, scroll position and unsaved drafts. Without an opener — a bookmark, a shared link,
 * a tab the browser restored — there is nothing to return to, so this window navigates to the
 * suite's dashboard instead. Same destination for the user either way; only the mechanics differ.
 *
 * The opener is cross-origin (the console lives on its own origin). `focus()`, `close` and
 * `closed` are the WindowProxy members the platform allows across origins; nothing else is read
 * from it, and nothing is sent to it. That also means this cannot check WHO the opener is — it
 * does not need to: focusing a window and closing our own is harmless whoever opened us.
 */

/** The legacy landing page, under the first account (`/u/0/dashboard`, legacy-admin-facts.md). */
export const SUITE_DASHBOARD_PATH = '/u/0/dashboard';

export interface ReturnTarget {
  /** The legacy admin's origin for this environment, e.g. `https://app.emil.de`. */
  readonly suiteUrl: string;
}

/** The slice of `window` this needs, so a test can hand in a fake. */
export interface ReturnWindow {
  readonly opener: { focus: () => void; readonly closed: boolean } | null;
  close: () => void;
  location: { assign: (url: string) => void };
}

export type ReturnOutcome = 'focused-opener' | 'navigated';

export function suiteDashboardUrl(suiteUrl: string): string {
  return `${suiteUrl.replace(/\/+$/, '')}${SUITE_DASHBOARD_PATH}`;
}

export function returnToSuite(target: ReturnTarget, win: ReturnWindow): ReturnOutcome {
  const opener = win.opener;
  if (opener !== null && !opener.closed) {
    opener.focus();
    win.close();
    return 'focused-opener';
  }
  win.location.assign(suiteDashboardUrl(target.suiteUrl));
  return 'navigated';
}

/** The browser's own window, typed down to what `returnToSuite` touches. */
export function browserWindow(): ReturnWindow {
  return globalThis.window;
}
